/** @format */

import {
  Box,
  Button,
  Stack,
  Typography,
  Unstable_Grid2 as Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import useTitle from "Hooks/useTitle";
import { getRules, RegisterSudent } from "Services/Student";
import { Alert } from "utils/alerts";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "components/LangSwitch";

const SignUp = () => {
  const { t } = useTranslation();
  const [document, setDocument] = useState(null);
  const [isForeign, setIsForeign] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  const navigate = useNavigate();
  useTitle(t("register.title"));

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    getValues,
    clearErrors,
    setError,
    control,
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  useEffect(() => {
    watch();
  }, []);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file.size > 30 * 1024 * 1024) {
      setError("document", {
        type: "manual",
        message: t("register.fileSize"),
      });
      return;
    }
    setDocument(file);
    clearErrors("document");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
  });
  const { lang } = useParams(); // read /:lang
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang !== "fa" && lang !== "en") {
      i18n.changeLanguage("fa"); // default
      document.cookie = `i18next=${lang}; path=/; domain=.copysa.ir; max-age=31536000`;

    } else {
      i18n.changeLanguage(lang);
      document.cookie = `i18next=${lang}; path=/; domain=.copysa.ir; max-age=31536000`;

    }
  }, [lang, i18n]);

  const onSubmit = async (data) => {
    if (!document) {
      setError("document", {
        type: "manual",
        message: t("register.fileRequired"),
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("fullname", data.fullname);
      formData.append(
        "nationalcode",
        isForeign ? "0" : data.nationalcode || ""
      );
      formData.append("email", data.email);
      formData.append("cardnumber", data.cardnumber);
      formData.append("address", data.address);
      formData.append("isForeignnationals", isForeign.toString());
      formData.append("file", document);

      const response = await RegisterSudent(formData);

      await Swal.fire({
        title: t("register.registerSuccessTitle"),
        text: t("register.registerSuccessText"),
        icon: "success",
        confirmButtonText: t("register.registerSuccessConfirm"),
      });

      navigate("/");
    } catch (error) {
      Alert(
        t("newFile.error"),
        error?.response?.data,
        "error",
        t("newFile.submit")
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
      }}
    >
      {/* Left Side - Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, sm: 3 },

          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 550 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 2,
              boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
              p: { xs: 2 },
            }}
          >
            <Box
              sx={{
                mb: { xs: 1 },
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  mb: 1,
                }}
              >
                {t("register.title")}
              </Typography>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={{ xs: 2 }}>
                <TextField
                  fullWidth
                  label={t("register.fullname")}
                  variant="outlined"
                  error={!!formErrors.fullname}
                  InputLabelProps={{
                    shrink: getValues().fullname ? true : false,
                  }}
                  helperText={formErrors.fullname?.message?.toString() ?? ""}
                  {...register("fullname", {
                    required: t("register.fullnameRequired"),
                  })}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    gap: 2,
                    flexDirection: "column",
                  }}
                >
                  <TextField
                    fullWidth
                    type="number"
                    label={t("register.nationalcode")}
                    InputLabelProps={{
                      shrink: getValues().nationalcode ? true : false,
                    }}
                    variant="outlined"
                    error={!!formErrors.nationalcode}
                    helperText={
                      formErrors.nationalcode?.message?.toString() ?? ""
                    }
                    disabled={isForeign}
                    {...register("nationalcode", {
                      validate: (value) => {
                        if (!isForeign && !value)
                          return t("register.nationalcodeRequired");
                        if (!isForeign && !/^[\d]{10}$/.test(value))
                          return t("register.nationalcodePattern");
                        return true;
                      },
                    })}
                  />
                  <FormControlLabel
                    sx={{ pr: 3 }}
                    control={
                      <Checkbox
                        checked={isForeign}
                        onChange={(e) => {
                          setIsForeign(e.target.checked);
                          if (e.target.checked) {
                            setValue("nationalcode", " ");
                          }
                        }}
                      />
                    }
                    label={t("register.foreign")}
                  />
                </Box>

                <TextField
                  fullWidth
                  type="email"
                  dir="ltr"
                  label={t("register.email")}
                  variant="outlined"
                  {...register("email", {
                    required: t("register.emailRequired"),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("register.emailPattern"),
                    },
                  })}
                  error={!!formErrors.email}
                  helperText={formErrors.email?.message?.toString() ?? ""}
                  InputLabelProps={{ shrink: Boolean(getValues("email")) }}
                />
                <TextField
                  fullWidth
                  dir="ltr"
                  label={t("register.cardnumber")}
                  InputLabelProps={{
                    shrink: getValues().cardnumber ? true : false,
                  }}
                  variant="outlined"
                  error={!!formErrors.cardnumber}
                  helperText={formErrors.cardnumber?.message?.toString() ?? ""}
                  {...register("cardnumber", {
                    required: t("register.cardnumberRequired"),
                    pattern: {
                      value: /^(\d{16})$/,
                      message: t("register.cardnumberPattern"),
                    },
                  })}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  dir="rtl"
                  label={t("register.address")}
                  InputLabelProps={{
                    shrink: getValues().address ? true : false,
                  }}
                  variant="outlined"
                  error={!!formErrors.address}
                  helperText={formErrors.address?.message?.toString() ?? ""}
                  {...register("address", {
                    required: t("register.addressRequired"),
                    maxLength: {
                      value: 500,
                      message: t("register.addressMax"),
                    },
                  })}
                />

                <Box
                  {...getRootProps()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragActive
                      ? "primary.main"
                      : formErrors.document
                      ? "error.main"
                      : "grey.400",
                    borderRadius: 2,
                    p: { xs: 1.5, sm: 2 },
                    textAlign: "center",
                    bgcolor: isDragActive ? "grey.100" : "white",
                    height: { xs: "100px", sm: "100px" },
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: { xs: 36, sm: 48 } }} />
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    {isDragActive
                      ? t("register.fileDropHere")
                      : document
                      ? t("register.fileSelected", { name: document.name })
                      : t("register.studentCardOrId")}
                  </Typography>
                </Box>

                {formErrors.document && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {formErrors.document.message?.toString()}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    "&:hover": {
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {t("register.submit")}
                </Button>
                <Grid xs={12}>
                  <Controller
                    name="termsAccepted"
                    control={control}
                    rules={{
                      required: t("register.termsRequired"),
                    }}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />

                  <Typography component="span" dir="rtl">
                    <Trans
                      i18nKey="register.termsWithLabel"
                      components={{
                        label: (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setRulesOpen(true);
                            }}
                            style={{
                              color: "#512c63",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          />
                        ),
                      }}
                    />
                  </Typography>
                  {formErrors.termsAccepted && (
                    <Typography variant="body2" sx={{ color: "red" }}>
                      {formErrors.termsAccepted.message?.toString()}
                    </Typography>
                  )}
                </Grid>
              </Stack>
            </form>
            <Dialog
              open={rulesOpen}
              onClose={() => setRulesOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>{t("register.rulesDialogTitle")}</DialogTitle>
              <DialogContent dividers sx={{ maxHeight: "675px" }}>
                <ul style={{ listStyleType: "disc", paddingRight: "30px" }}>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.introduction.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.introduction.content")}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.definitions.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.definitions.platform")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.definitions.uploader")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.definitions.notes")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.definitions.wallet")}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.general_conditions.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.general_conditions.compliance")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.general_conditions.identity")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.general_conditions.misuse")}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.upload_and_sale.intellectual_property"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.upload_and_sale.permitted_content"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.allowed_formats")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.pricing")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.earnings")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.pricing_example")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.content_review")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.upload_and_sale.platform_revenue")}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.wallet_and_transactions.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.wallet_deposit"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.withdrawal_request"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.instant_settlement"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.monthly_settlement"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.processing_time"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.wallet_and_transactions.transaction_responsibility"
                      )}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.security_and_privacy.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.security_and_privacy.data_protection"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.security_and_privacy.password_confidentiality"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.security_and_privacy.sensitive_information"
                      )}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.responsibilities_and_limitations.title"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.responsibilities_and_limitations.platform_role"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.responsibilities_and_limitations.copyright_complaints"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.responsibilities_and_limitations.service_termination"
                      )}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.account_termination.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.account_termination.violation_consequences"
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t(
                        "TermsAndConditions.account_termination.wallet_balance"
                      )}
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "12px" }}>
                    <Typography
                      variant="h6"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.contact_us.title")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {t("TermsAndConditions.contact_us.content")}
                    </Typography>
                  </li>
                </ul>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRulesOpen(false)} variant="contained">
                  {t("register.rulesDialogClose")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </Box>

      {/* Right Side - Welcome */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", lg: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: "column",
          background: "linear-gradient(135deg, #2F0743 0%, #41295a 100%)",
          position: "relative",
          py: 3,
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at center, rgba(72, 21, 183, 0.1) 0%, transparent 70%)",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            px: 5,
          }}
        >
          <LanguageSwitcher />
        </Box>
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            maxWidth: "600px",
            px: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            {t("register.welcome")}
          </Typography>
        </Box>
        <Box />
      </Box>
    </Box>
  );
};

export default SignUp;
